import React, { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Popconfirm, theme, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { RootState } from 'store/configureStore';
import { managementOp } from 'store/ducks/Management';
import CustomTable from 'components/Tables/CustomTable';
import DataManagementLayout from 'components/Layouts/DataManagementLayout/DataManagementLayout';
import { ISolutionType } from 'store/ducks/Management/types';
import {
  SOLUTION_TYPE_COLUMNS,
  SOLUTION_TYPE_KEYS
} from 'constants/solutionTypeColumns';
import { useUnsavedChanges } from 'utils/useUnsavedChanges';
import * as Styled from './SolutionTypeManagement.styled';

const { useToken } = theme;

const SolutionTypeManagement: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { token } = useToken();

  const dispatch = useDispatch();
  const { drawTable, lang, saveClicked } = useSelector((state: RootState) => ({
    drawTable: state.management.drawTable,
    lang: state.global.lang,
    saveClicked: state.management.saveClicked
  }));

  const [rows, setRows] = useState<ISolutionType[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [initialRows, setInitialRows] = useState<ISolutionType[]>([]);

  useUnsavedChanges({ hasChanges, data: rows, initialData: initialRows });

  useEffect(() => {
    if (rows.length > 0 && initialRows.length === 0) {
      setInitialRows(rows);
    }
  }, [rows, initialRows]);

  useEffect(() => {
    if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  const handleFieldChange = (
    rowKey: string,
    field: keyof ISolutionType,
    value: string
  ) => {
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.key === rowKey ? { ...row, [field]: value } : row
      )
    );
    setHasChanges(true);
    dispatch(managementOp.setDataEdited(true));
  };

  const handleSave = async () => {
    try {
      const validRows = rows.filter(
        (row) => row.short_name.trim() || row.description.trim()
      );

      if (validRows.length === 0) {
        message.info('No changes to save');
        return;
      }

      // TODO: Implement API call for solution types
      message.success('Solution types saved successfully!');
      setHasChanges(false);
      dispatch(managementOp.setDataEdited(false));
      setInitialRows(rows);
    } catch (error) {
      console.error('Save failed:', error);
      message.error('Failed to save solution types');
    }
  };

  useEffect(() => {
    if (saveClicked) {
      handleSave();
      dispatch(managementOp.setSaveClicked(false));
    }
  }, [saveClicked]);

  const handleAddRow = () => {
    const newRow: ISolutionType = {
      key: `new_${Date.now()}`,
      short_name: '',
      description: ''
    };
    setRows((prevRows) => [...prevRows, newRow]);
    setHasChanges(true);
    dispatch(managementOp.setDataEdited(true));
  };

  const handleDeleteClick = async (id: number) => {
    try {
      // TODO: Implement API call for deleting solution type
      setRows((prevRows) => prevRows.filter((row) => row.id !== id));
      message.success('Solution type deleted successfully!');
    } catch (error) {
      console.error('Delete failed:', error);
      message.error('Failed to delete solution type');
    }
  };

  const columns: ColumnsType<ISolutionType> = useMemo(
    () => [
      {
        title: t(SOLUTION_TYPE_COLUMNS.ACTION),
        key: SOLUTION_TYPE_KEYS.ACTION,
        width: 100,
        render: (_, record) => (
          <Styled.StyledButtonSection token={token}>
            <Popconfirm
              title='Delete Solution Type'
              description='Are you sure you want to delete this solution type?'
              onConfirm={() => record.id && handleDeleteClick(record.id)}
              okText='Yes'
              cancelText='No'
              disabled={!record.id}
            >
              <Styled.StyledButton
                type='primary'
                size='small'
                shape='circle'
                icon={<DeleteOutlined />}
                disabled={!record.id}
              />
            </Popconfirm>
          </Styled.StyledButtonSection>
        )
      },
      {
        title: t(SOLUTION_TYPE_COLUMNS.SHORT_NAME),
        dataIndex: SOLUTION_TYPE_KEYS.SHORT_NAME,
        key: SOLUTION_TYPE_KEYS.SHORT_NAME,
        width: 425,
        render: (val, data) => (
          <Styled.ColumnTextBox
            width={500}
            value={data.short_name}
            placeholder='Enter solution type name'
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              handleFieldChange(data.key!, 'short_name', e.target.value)
            }
          />
        )
      },
      {
        title: t(SOLUTION_TYPE_COLUMNS.DESCRIPTION),
        dataIndex: SOLUTION_TYPE_KEYS.DESCRIPTION,
        key: SOLUTION_TYPE_KEYS.DESCRIPTION,
        width: 425,
        render: (val, data) => (
          <Styled.ColumnTextBox
            width={425}
            value={data.description}
            placeholder='Enter solution type description'
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              handleFieldChange(data.key!, 'description', e.target.value)
            }
          />
        )
      }
    ],
    [t, token]
  );

  return (
    <DataManagementLayout>
      <Styled.ContentWrapper>
        {drawTable ? (
          <>
            <CustomTable
              drawTable={drawTable}
              columns={columns}
              dataSource={rows}
              itemsCount={rows.length}
              pagination={false}
              scroll={{ x: 1000 }}
            />
            <Button
              type='primary'
              onClick={handleAddRow}
              icon={<PlusOutlined />}
              shape='circle'
            />
          </>
        ) : (
          <CustomTable
            drawTable={drawTable}
            itemsCount={0}
            columns={columns}
            dataSource={[]}
            pagination={false}
          />
        )}
      </Styled.ContentWrapper>
    </DataManagementLayout>
  );
};

export default SolutionTypeManagement;
